import type { MyApprovalItem } from "../services/financialService";

export const APPROVAL_PAGE_SIZES = [10, 25, 50];

export type ApprovalFilters = {
  search: string;
  /** A workflow status, or "" for everything in the queue. */
  status: string;
  /** A document type such as "financial.vendorbill", or "" for every kind. */
  documentType: string;
};

export const EMPTY_APPROVAL_FILTERS: ApprovalFilters = {
  search: "",
  status: "",
  documentType: "",
};

export const APPROVAL_STATUS_DOTS: Record<string, string> = {
  submitted: "bg-amber-500",
  verified: "bg-blue-500",
  pending: "bg-amber-500",
};

export function activeApprovalFilterCount(filters: ApprovalFilters) {
  return Object.values(filters).filter((value) => value.trim()).length;
}

/** "All" plus each status actually in the queue, in the order they first appear. */
export function approvalStatusTabs(items: MyApprovalItem[]): [string, string][] {
  const statuses = [...new Set(items.map((item) => item.workflow_status))];
  return [["", "All"], ...statuses.map((status): [string, string] => [status, status.replace(/_/g, " ")])];
}

export function approvalStatusCounts(items: MyApprovalItem[]) {
  const counts: Record<string, number> = { "": items.length };
  for (const item of items) counts[item.workflow_status] = (counts[item.workflow_status] ?? 0) + 1;
  return counts;
}

/** Queue entries matching the filters, kept in the server's oldest-first order.
 *
 *  `labels` names each document type, so searching "vendor bill" finds bills
 *  whose title only says BILL-000004. */
export function filterApprovals(
  items: MyApprovalItem[],
  filters: ApprovalFilters,
  labels: Record<string, string> = {},
  { ignoreStatus = false } = {},
) {
  const term = filters.search.trim().toLowerCase();
  return items.filter(
    (item) =>
      (ignoreStatus || !filters.status || item.workflow_status === filters.status) &&
      (!filters.documentType || item.document_type === filters.documentType) &&
      (!term ||
        [item.title, item.initiated_by, labels[item.document_type] ?? item.document_type].some((value) =>
          (value ?? "").toLowerCase().includes(term),
        )),
  );
}

/** A detail-table cell that holds a figure ("1,200,000.00", "-3.5"), so it can be right-aligned. */
export const isFigure = (cell: string) => /^-?[\d,]+(\.\d+)?%?$/.test(cell.trim());
